import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useAdminAccess from '../context/useAdminAccess';
import { API_BASE_URL, APP_VERSION } from '../config';
import { formatLongDate } from '../lib/formatDate';
import useNavItems, { NavIcons } from './navItems';
import { useManagedDepartments } from './department/MyDepartmentsNav';

// One slim row on every employee page: greeting, the date beside it, then the
// account menu. On a phone (<=600px) the sidebar is hidden and this menu is the
// only navigation, so it also lists the sidebar's destinations (the
// `.topbar-dropdown-nav` block, shown by CSS at that width only) from the same
// useNavItems list, so the two can never offer different places.
export default function TopBar() {
  const { user } = useAuth();
  const { isFullAdmin, hasAdminAccess } = useAdminAccess();
  const { home, items, isDeptManager } = useNavItems();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const triggerRef = useRef(null);
  const wrapRef = useRef(null);
  // Whether keyboard focus was last inside the menu. Not cleared by a blur to
  // nowhere (a click on page background), so that click can hand focus back
  // to the avatar instead of dropping it on <body>.
  const focusInsideRef = useRef(false);
  // Same source as the sidebar's My Departments group: one link per managed
  // department, since on a phone the sidebar (and its per-department list) is
  // hidden. Fetched only for managers; the server 403s everyone else.
  const managedDepts = useManagedDepartments(isDeptManager);

  const handleLogout = async () => {
    // POST so cross-site links/images can't force a logout. Cookie travels
    // via credentials: include; sameSite=lax blocks third-party POSTs from
    // attaching the session cookie.
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (_) { /* fall through to redirect regardless */ }
    window.location.href = '/';
  };

  const close = () => setDropdownOpen(false);

  // Any navigation (sidebar, a card link, back button) closes the menu.
  useEffect(() => { setDropdownOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      setDropdownOpen(false);
      // Focus was in the menu and the click landed on nothing focusable: put
      // it back on the trigger rather than losing it to <body>.
      const active = document.activeElement;
      if (focusInsideRef.current && (!active || active === document.body)) {
        triggerRef.current?.focus({ preventScroll: true });
      }
      focusInsideRef.current = false;
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dropdownOpen]);

  const onWrapFocus = () => { focusInsideRef.current = true; };
  // Tabbing out of the menu closes it, so it doesn't stay open over the page.
  // A blur with no relatedTarget (a click on background, or Safari not
  // focusing clicked links) is left to the click handler above.
  const onWrapBlur = (e) => {
    const next = e.relatedTarget;
    // Only a real element counts as "focus went elsewhere" (jsdom reports the
    // document for a blur to nowhere; browsers report null).
    const movedTo = next && next.nodeType === 1 && next !== document.body ? next : null;
    if (movedTo && !wrapRef.current?.contains(movedTo)) {
      focusInsideRef.current = false;
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setDropdownOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dropdownOpen]);

  const menuLink = (to, label, Icon, key = to, active = location.pathname === to) => (
    <Link
      key={key}
      to={to}
      className="topbar-dropdown-item"
      aria-current={active ? 'page' : undefined}
      onClick={close}
    >
      <Icon />
      {label}
    </Link>
  );

  // One link per managed department; before the list arrives (or if it is
  // empty or failed) the /dept landing link, which picks the first one.
  const deptLinks = () => {
    if (!isDeptManager) return null;
    if (managedDepts && managedDepts.length > 1) {
      return managedDepts.map((d) => menuLink(`/dept/${d.id}`, d.name, NavIcons.departments, `dept-${d.id}`));
    }
    const only = managedDepts && managedDepts.length === 1 ? managedDepts[0] : null;
    // One department: name it. Unknown yet: the generic label, to /dept.
    return menuLink(only ? `/dept/${only.id}` : '/dept', only ? only.name : 'My Departments', NavIcons.departments, 'dept',
      only ? location.pathname === `/dept/${only.id}` : location.pathname.startsWith('/dept'));
  };

  return (
    <header className="topbar">
      <div className="topbar-inner">
        <div className="topbar-left">
          <h1 className="topbar-greeting">
            Hello {user?.name?.split(' ')[0] || 'there'} <span className="topbar-wave">👋</span>
          </h1>
          <span className="topbar-date">{formatLongDate(new Date())}</span>
        </div>
        <div className="topbar-right">
          {hasAdminAccess && (
            <span className="topbar-admin-tag">{isFullAdmin ? 'Admin' : 'Admin access'}</span>
          )}
          <div className="topbar-avatar-wrap" ref={wrapRef} onFocus={onWrapFocus} onBlur={onWrapBlur}>
            <button
              ref={triggerRef}
              type="button"
              className={`topbar-avatar ${user?.photo_url ? 'has-photo' : ''}`}
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-expanded={dropdownOpen}
              aria-label={user?.name ? `Account menu for ${user.name}` : 'Account menu'}
            >
              {user?.photo_url ? (
                <img
                  src={user.photo_url}
                  alt=""
                  referrerPolicy="no-referrer"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor" />
                  <path d="M12 14C6.47715 14 2 18.4772 2 24H22C22 18.4772 17.5228 14 12 14Z" fill="currentColor" />
                </svg>
              )}
            </button>
            <div className={`topbar-dropdown ${dropdownOpen ? 'show' : ''}`}>
              {menuLink('/', home.label || 'Home', home.Icon)}
              {menuLink('/profile', 'My profile', ProfileIcon)}
              {/* Phone only (CSS): the sidebar's destinations, since the
                  sidebar itself is hidden at that width. */}
              <div className="topbar-dropdown-nav">
                {items.map(({ key, to, label, Icon, isActive }) => menuLink(to, label, Icon, key, isActive(location.pathname)))}
                {deptLinks()}
              </div>
              {hasAdminAccess && menuLink('/admin', 'Admin Dashboard', AdminIcon, '/admin', false)}
              <button
                type="button"
                className="topbar-dropdown-item"
                onClick={() => { close(); handleLogout(); }}
              >
                <LogoutIcon />
                Logout
              </button>
              {APP_VERSION && (
                <span className="topbar-dropdown-version" title={`Nexus version ${APP_VERSION}`}>
                  Nexus v{APP_VERSION}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

const iconProps = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2', 'aria-hidden': 'true' };

function ProfileIcon() {
  return (
    <svg {...iconProps}>
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function AdminIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg {...iconProps}>
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
  );
}
